import { useEffect, useState, useMemo } from "react";
import { FixedSizeList as VirtualList } from "react-window";
import ContainerModalActions from "../../../../Containers/ContainerModalActions";
import {
  BUTTON_GO_BACK,
  BUTTON_SAVE,
  MODAL_EDIT_PRODUCTS_CONFIGURATION_DESCRIPTION,
  MODAL_EDIT_PRODUCTS_CONFIGURATION_TITLE,
  PRODUCTS_CONFIGURATION_SYNC_DELAY_CAPTION,
  ALL_PRODUCTS,
  USE_A_SELECTION_OF_PRODUCTS,
  SELECT_PRODUCTS_BY,
  PRODUCTS,
  EMPTY_RESULTS_SHOW,
  SELECTED,
  SELECT_ALL,
  SEARCH_PLACEHOLDER,
} from "../../../../../i18n/keysTranslations";
import ModalBasicLayout from "../../../../Modal/ModalBasicLayout";
import { useTranslationApp } from "../../../../../lib/i18next";
import {
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  MenuItem,
  Select,
  List,
  ListItem,
  ListItemText,
  Typography,
  ListItemIcon,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Checkbox from "../../../../Form/Checkbox";
import { startGetMerchantProductsGoogleAds } from "../../../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../../../lib/redux";
import Loader from "../../../../Loaders/Loader";
import {
  SUB_DIVISIONS_WITH_COUNT_GOOGLE,
  SUBDIVISION_OPTIONS_GOOGLE,
} from "../../../../../services/google";
import { Search } from "@mui/icons-material";
import { BORDER_RADIUS } from "../../../../../utils/constants";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../../utils/colors";

const ModalEditProductsConfiguration = ({
  modalOpen,
  onCloseModal,
  initialValues = {},
  onCallbackSave = () => {},
  integrationID,
  adAccountID,
  merchantCenterID,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  // State for the configuration object
  const [config, setConfig] = useState(() => ({
    type: initialValues.type || "allProducts",
    subdivision: initialValues.subdivision || "productCategory",
    children: initialValues.children || [],
  }));

  // Generate dynamic options for subdivisions based on products
  const getDynamicSubdivisionOptions = () => {
    // Helper to get unique values
    const getUnique = (arr, key) => {
      const set = new Set();
      arr.forEach((item) => {
        if (item[key]) set.add(item[key]);
      });
      return Array.from(set).map((val) => ({ label: val, value: val }));
    };

    // Helper for custom labels (may not exist in all products)
    const getCustomLabel = (products, labelKey) => {
      const set = new Set();
      products.forEach((item) => {
        if (item[labelKey]) set.add(item[labelKey]);
      });
      return Array.from(set).map((val) => ({ label: val, value: val }));
    };

    return SUBDIVISION_OPTIONS_GOOGLE.map((sub) => {
      if (sub.value === "productBrand") {
        return { ...sub, options: getUnique(products, "brand") };
      }
      if (sub.value === "itemId") {
        const options = products.map((p) => ({
          label: `${p.name} (${p.id})`,
          value: p.id,
        }));
        return { ...sub, options };
      }
      if (sub.value === "productType") {
        return { ...sub, options: getUnique(products, "productType") };
      }
      if (sub.value.startsWith("customLabel")) {
        return { ...sub, options: getCustomLabel(products, sub.value) };
      }
      return sub;
    });
  };

  const dynamicSubdivisionOptions = getDynamicSubdivisionOptions();
  const currentSubdivision =
    dynamicSubdivisionOptions.find((opt) => opt.value === config.subdivision) ||
    dynamicSubdivisionOptions[0];

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    return currentSubdivision.options.filter((option) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const optionLabel = currentSubdivision.translateOptions
        ? t(option.label).toLowerCase()
        : option.label.toLowerCase();
      return optionLabel.includes(searchLower);
    });
  }, [
    currentSubdivision.options,
    searchTerm,
    currentSubdivision.translateOptions,
    t,
  ]);

  // Handlers
  const onTypeChange = (e) => {
    const newType = e.target.value;
    setConfig((prev) => ({
      ...prev,
      type: newType,
      subdivision: "productCategory",
      children: [],
    }));
  };

  const onSubdivisionChange = (e) => {
    setConfig((prev) => ({
      ...prev,
      subdivision: e.target.value,
      children: [],
    }));
  };

  const onChildrenToggle = (option) => {
    setConfig((prev) => {
      const exists = prev.children.includes(option.value);
      return {
        ...prev,
        children: exists
          ? prev.children.filter((child) => child !== option.value)
          : [...prev.children, option.value],
      };
    });
  };

  const onSelectAll = () => {
    if (config.children.length === filteredOptions.length) {
      setConfig((prev) => ({ ...prev, children: [] }));
    } else {
      setConfig((prev) => ({
        ...prev,
        children: [...filteredOptions.map((opt) => opt.value)],
      }));
    }
  };

  // Virtualized list item renderer
  const VirtualizedListItem = ({ index, style }) => {
    const option = filteredOptions[index];
    if (!option) return null;

    return (
      <ListItem
        style={style}
        onClick={() => onChildrenToggle(option)}
        sx={{ cursor: "pointer", pl: 0 }}
      >
        <ListItemIcon>
          <Checkbox
            checked={config.children.includes(option.value)}
            tabIndex={-1}
            disableRipple
            inputProps={{
              "aria-labelledby": `checkbox-list-label-${option.value}`,
            }}
          />
        </ListItemIcon>
        <ListItemText
          id={`checkbox-list-label-${option.value}`}
          primary={
            <Tooltip
              title={
                <Box>
                  <Typography variant="body1">
                    {currentSubdivision.translateOptions
                      ? t(option.label)
                      : option.label}
                  </Typography>
                  {SUB_DIVISIONS_WITH_COUNT_GOOGLE.includes(
                    currentSubdivision.value
                  ) && (
                    <Typography variant="body2">
                      {`${getOptionCount(
                        currentSubdivision.value,
                        option.value
                      )} ${t(PRODUCTS)}`}
                    </Typography>
                  )}
                </Box>
              }
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {currentSubdivision.translateOptions
                    ? t(option.label)
                    : option.label}
                </Typography>
                {SUB_DIVISIONS_WITH_COUNT_GOOGLE.includes(
                  currentSubdivision.value
                ) && (
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`${getOptionCount(
                      currentSubdivision.value,
                      option.value
                    )} ${t(PRODUCTS)}`}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          }
        />
      </ListItem>
    );
  };

  const onSave = () => {
    if (config.type === "specific" && config.children.length === 0) {
      onCallbackSave({
        type: "allProducts",
        subdivision: "",
        children: [],
      });
      return;
    }
    onCallbackSave(config);
  };

  const onGetProducts = async () => {
    setIsLoading(true);
    const response = await dispatch(
      startGetMerchantProductsGoogleAds({
        integrationID,
        adAccountID,
        merchantCenterID,
      })
    );
    if (response.ok) setProducts(response.data);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!merchantCenterID) return;

    onGetProducts();
    // eslint-disable-next-line
  }, []);

  // Función para obtener el count de productos por valor de subdivisión
  const getOptionCount = (subdivision, value) => {
    if (!products || !products.length) return 0;
    switch (subdivision) {
      case "productCategory":
        // El valor de category en products puede tener prefijo, por ejemplo: 'productCategoryConstants/LEVEL1~166'
        return products.filter(
          (p) =>
            p.category &&
            (p.category === `productCategoryConstants/${value}` ||
              p.category === value ||
              p.category.endsWith(`/${value}`))
        ).length;
      case "productBrand":
        return products.filter((p) => p.brand === value).length;
      case "condition":
        return products.filter((p) => p.condition === value).length;
      case "productType":
        return products.filter((p) => p.productType === value).length;
      case "customLabel0":
      case "customLabel1":
      case "customLabel2":
      case "customLabel3":
      case "customLabel4":
        return products.filter((p) => p[subdivision] === value).length;
      default:
        return null;
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={async () => {
        onCloseModal();
      }}
      maxWidth="md"
      scroll="paper"
      title={t(MODAL_EDIT_PRODUCTS_CONFIGURATION_TITLE)}
      description={t(MODAL_EDIT_PRODUCTS_CONFIGURATION_DESCRIPTION)}
    >
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <Box sx={{ mb: 1 }}>
            <FormControl component="fieldset">
              <RadioGroup row value={config.type} onChange={onTypeChange}>
                <FormControlLabel
                  value="allProducts"
                  control={<Radio />}
                  label={t(ALL_PRODUCTS)}
                />
                <FormControlLabel
                  value="specific"
                  control={<Radio />}
                  label={t(USE_A_SELECTION_OF_PRODUCTS)}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Subdivision and selection list */}
          {config.type === "specific" && (
            <Box>
              <Box
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Typography variant="subtitle1">
                  {t(SELECT_PRODUCTS_BY)}:
                </Typography>
                <Select
                  value={config.subdivision}
                  onChange={onSubdivisionChange}
                  size="small"
                >
                  {dynamicSubdivisionOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {`${t(opt.label)} ${opt.subLabel || ""}`}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                {t(PRODUCTS_CONFIGURATION_SYNC_DELAY_CAPTION)}
              </Typography>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Box sx={{ width: "70%" }}>
                  {/* Search field */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t(SEARCH_PLACEHOLDER)}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      mb: 2,
                      backgroundColor: "fields.background",
                      borderRadius: BORDER_RADIUS,
                      color: "#000 !important",
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                      boxShadow: BOX_SHADOW_FIELDS,
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                        {
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                        },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                        },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Select All checkbox */}
                  <Box>
                    <ListItem
                      onClick={
                        filteredOptions.length === 0 ? undefined : onSelectAll
                      }
                      sx={{
                        p: 0,
                        opacity: filteredOptions.length === 0 ? 0.5 : 1,
                        pointerEvents:
                          filteredOptions.length === 0 ? "none" : "auto",
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={
                            filteredOptions.length > 0
                              ? config.children.length ===
                                filteredOptions.length
                              : false
                          }
                          indeterminate={
                            filteredOptions.length > 0 &&
                            config.children.length > 0 &&
                            config.children.length < filteredOptions.length
                          }
                          disabled={filteredOptions.length === 0}
                          tabIndex={-1}
                          inputProps={{ "aria-label": t(SELECT_ALL) }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={t(SELECT_ALL)} />
                    </ListItem>
                  </Box>

                  {/* Virtualized list container */}
                  <Box
                    sx={{
                      height: 250,
                      overflow: "hidden",
                    }}
                  >
                    {filteredOptions.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          p: 2,
                        }}
                      >
                        <Typography>{t(EMPTY_RESULTS_SHOW)}</Typography>
                      </Box>
                    ) : (
                      <VirtualList
                        height={250}
                        itemCount={filteredOptions.length}
                        itemSize={40}
                        width="100%"
                      >
                        {VirtualizedListItem}
                      </VirtualList>
                    )}
                  </Box>
                </Box>
                <Box sx={{ width: "30%", maxHeight: 250, overflow: "auto" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t(SELECTED)}:
                  </Typography>
                  <List dense>
                    {config.children.length === 0 && (
                      <ListItem
                        sx={{
                          pl: 0,
                        }}
                      >
                        <ListItemText primary={t(EMPTY_RESULTS_SHOW)} />
                      </ListItem>
                    )}
                    {config.children.map((selected) => {
                      const found = currentSubdivision.options.find(
                        (opt) => opt.value === selected
                      );
                      return (
                        <ListItem
                          key={selected}
                          sx={{
                            pl: 0,
                          }}
                        >
                          <ListItemText
                            primary={
                              found
                                ? currentSubdivision.translateOptions
                                  ? t(found.label)
                                  : found.label
                                : selected
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            disabled: isLoading,
            text: BUTTON_GO_BACK,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: isLoading,
            text: BUTTON_SAVE,
            onClick: onSave,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalEditProductsConfiguration;
